import { inject, Injectable, Type } from '@angular/core';
import { ConfirmationService } from '@openng/optimus-ui/api';
import { DialogService as PDialogService, DynamicDialogConfig } from '@openng/optimus-ui/dynamicdialog';
import { combineLatest, Observable, of } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { MsgKeyService } from '../../translate/msg-key.service';
import { DialogOptions } from './models/dialog-options';

@Injectable()
export class DialogService {

	private options?: DialogOptions[] = [];

	private readonly msgKeyService = inject(MsgKeyService);
	private readonly confirmationService = inject(ConfirmationService);
	private readonly pDialogService = inject(PDialogService);

	/**
	 * Kann verwendet werden, um msgKeys vor confirm() zu laden
	 * @param options
	 * @return Index, der an confirm() übergeben wird
	 */
	public precache(options: DialogOptions): number {
		options.messageKeyCached = true;
		options.cancelKey = options.cancelKey || 'button.cancel.alt';
		options.okKey = options.okKey || 'button.ok.alt';

		if (options.messageKey) {
			this.msgKeyService.getMessageWithCache(options.messageKey, options.messageReplacements)
				.subscribe();
		} else if (!options.message) {
			throw new Error('needs either message or message key');
		}
		if (options.titleKey) {
			this.msgKeyService.getMessageWithCache(options.titleKey, options.titleReplacements)
				.subscribe();
		} else if (!options.title) {
			throw new Error('needs either title or title key');
		}

		this.msgKeyService.getMessageWithCache(options.cancelKey)
			.subscribe();

		this.msgKeyService.getMessageWithCache(options.okKey)
			.subscribe();

		return this.options.push(options) - 1;
	}

	/**
	 * Öffnet einen Bestätigungsdialog.
	 * @param options Wenn eine number übergeben wird, werden die prechached options anhand des Index verwendet. Default 0
	 * @return Observable mit boolschem Wert, ob bestätigt oder abgelehnt wurde.
	 */
	confirm(options?: DialogOptions | number): Observable<boolean> {
		let dialogOptions: DialogOptions;
		if (typeof options === 'number') {
			dialogOptions = this.options[options];
		} else if (options) {
			dialogOptions = options;
		} else {
			dialogOptions = this.options[0];
		}

		if (dialogOptions.messageKeyCached === undefined) {
			dialogOptions.messageKeyCached = true;
		}

		const titleOption = dialogOptions.title
			? of(dialogOptions.title)
			: dialogOptions.titleKey
				? this.msgKeyService.getMessageWithCache(dialogOptions.titleKey, dialogOptions.titleReplacements)
				: of(null);
		const messageOption = dialogOptions.message
			? of(dialogOptions.message)
			: dialogOptions.messageKeyCached
				? this.msgKeyService.getMessageWithCache(dialogOptions.messageKey, dialogOptions.messageReplacements)
				: this.msgKeyService.getMessage(dialogOptions.messageKey, dialogOptions.messageReplacements);
		const cancelOption = dialogOptions.cancel
			? of(dialogOptions.cancel)
			: dialogOptions.cancel === false
				? of(null)
				: this.msgKeyService.getMessageWithCache(dialogOptions.cancelKey || 'button.cancel.alt');
		const okOption = dialogOptions.ok
			? of(dialogOptions.ok)
			: this.msgKeyService.getMessageWithCache(dialogOptions.okKey || 'button.ok.alt');

		return new Observable<boolean>(subscriber => {
			combineLatest([titleOption, cancelOption, messageOption, okOption])
				.pipe(take(1))
				.subscribe(([header, rejectLabel, message, acceptLabel]) => {
					const rejectable = rejectLabel !== null;
					this.confirmationService.confirm({
						message,
						header,
						rejectLabel,
						rejectVisible: rejectable,
						closable: rejectable,
						acceptLabel,
						acceptButtonProps: {
							severity: 'primary',
						},
						accept: () => {
							subscriber.next(true);
							subscriber.complete();
						},
						reject: () => {
							subscriber.next(false);
							subscriber.complete();
						},
					});
				});

			return () => this.confirmationService.close();
		});
	}

	/**
	 * Wrapper für Optimus DialogService.open() für Unterstützung von MessageKeys als header.
	 * Setzt defaults für `modal = true`, `closable = true`, `resizable/draggable = false`.
	 * Wenn mehr Funktionalität als `onClose` von DynamicDialogRef gebraucht wird, muss der Optimus DialogService direkt verwendet werden.
	 *
	 * @return DynamicDialogRef.onClose
	 */
	open<T, DataType = any, InputValuesType extends Record<string, any> = {}>(
		componentType: Type<T>,
		config: ExtendedDynamicDialogConfig<DataType, InputValuesType>,
	): Observable<any> {
		const titleOption = config.header
			? of(config.header)
			: config.headerKey
				? this.msgKeyService[`getMessage${ config.headerKeyCached
					? 'WithCache'
					: '' }`](config.headerKey, config.headerReplacements)
				: of(undefined);
		return titleOption.pipe(
			switchMap(header => this.pDialogService.open(componentType, {
				modal: true,
				resizable: false,
				draggable: false,
				closable: true,
				...config,
				header,
			}).onClose),
		);
	}
}

export interface ExtendedDynamicDialogConfig<DataType = any, InputValuesType extends Record<string, any> = {}>
	extends DynamicDialogConfig<DataType, InputValuesType> {
	headerKey?: string;
	headerReplacements?: Array<{
		[key: string]: string
	}>;
	headerKeyCached?: boolean;
}
